import Hls from "hls.js";

window.addEventListener("load", () => {
	/* =====================================================================
	   DISCLAIMER MODAL — br-blocks style
	===================================================================== */

	const overlay = document.createElement("div");
	overlay.className = "modal-overlay";
	document.body.appendChild(overlay);

	const modalContent = document.createElement("div");
	modalContent.className = "modal-content";
	document.body.appendChild(modalContent);

	modalContent.innerHTML = `
		<div class="modal-header">
			<div>
				<h3 class="modal-title">Disclaimers</h3>
			</div>
			<span class="br-modal-close" role="button" aria-label="Close">&times;</span>
		</div>
		<div class="scrollable-content wc-disclaimer-modal-body"></div>
	`;

	const modalBody    = modalContent.querySelector(".wc-disclaimer-modal-body");
	const closeBtn     = modalContent.querySelector(".br-modal-close");
	let   closingTimer = null;

	function openModal(contentEl) {
		modalBody.innerHTML = "";
		const clone = contentEl.cloneNode(true);
		clone.removeAttribute("hidden");
		clone.style.maxHeight = "";
		clone.style.overflow  = "";
		modalBody.appendChild(clone);

		clearTimeout(closingTimer);
		modalContent.classList.remove("is-closing");

		document.body.classList.add("modal-open");
		overlay.classList.add("is-visible");

		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				modalContent.classList.add("is-visible");
			});
		});
	}

	function closeModal() {
		overlay.classList.remove("is-visible");
		modalContent.classList.remove("is-visible");
		modalContent.classList.add("is-closing");

		closingTimer = setTimeout(() => {
			modalContent.classList.remove("is-closing");
			document.body.classList.remove("modal-open");
		}, 300);
	}

	overlay.addEventListener("click", closeModal);
	closeBtn.addEventListener("click", closeModal);

	document.addEventListener("keydown", (e) => {
		if (e.key === "Escape" && modalContent.classList.contains("is-visible")) {
			closeModal();
		}
	});

	document.querySelectorAll(".wc-disclaimer-toggle").forEach((btn) => {
		const contentEl = btn.nextElementSibling;
		if (!contentEl) return;
		btn.addEventListener("click", () => openModal(contentEl));
	});

	/* =====================================================================
	   HELPERS
	===================================================================== */

	function extractAllSessions(title) {
		if (!title) return [];
		const matches = [...title.matchAll(/Session\s+(\d+)/gi)];
		return matches.map((m) => `Session ${m[1]}`);
	}

	function formatDateLabel(dateStr) {
		const parts = dateStr.split("/");
		if (parts.length !== 3) return dateStr;
		const [day, month, year] = parts;
		const d = new Date(`${year}-${month}-${day}`);
		return d
			.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
			.replace(".", "");
	}

	function stopPlayer(inlineBox, video, scheduleBox, placeholder) {
		inlineBox.classList.remove("wc-inline-visible");
		inlineBox.classList.add("wc-inline-hidden");
		video.pause();

		if (video._hlsInstance) {
			video._hlsInstance.destroy();
			video._hlsInstance = null;
		}

		video.src = "";
		video.load();
		if (placeholder) placeholder.classList.remove("wc-placeholder-hidden");
		if (scheduleBox) {
			scheduleBox.scrollIntoView({ behavior: "smooth", block: "start" });
		}
	}

	function notifyPlaybackStart(sourceBlock) {
		document.dispatchEvent(new CustomEvent("wc-playback-start", {
			detail: { source: sourceBlock }
		}));
	}

	/* Tracks whether the user has ever interacted with this page.
	   Used to decide whether a play() attempt is likely to be allowed. */
	let _userHasInteracted = false;
	["pointerdown", "keydown", "touchstart"].forEach((ev) => {
		document.addEventListener(ev, () => { _userHasInteracted = true; }, { once: true, capture: true });
	});

	/* Attempts to play a <video>. If the browser blocks autoplay because the
	   user hasn't interacted with the page yet (typical deep-link case),
	   falls back to muted autoplay and shows a "tap to unmute" overlay
	   inside the given container. First real user interaction removes the
	   overlay and restores sound. */
	function tryPlayWithAutoplayFallback(videoEl, container) {
		const attempt = videoEl.play();
		if (!attempt || typeof attempt.then !== "function") return;

		attempt.catch(() => {
			/* First try failed. If user already clicked something, give up silently. */
			if (_userHasInteracted) return;

			videoEl.muted    = true;
			videoEl.volume   = 0;
			videoEl.setAttribute("muted", "");
			videoEl.setAttribute("playsinline", "");

			videoEl.play().then(() => {
				if (!container) return;

				/* Avoid stacking multiple overlays if called repeatedly. */
				let overlay = container.querySelector(".wc-unmute-overlay");
				if (overlay) return;

				overlay = document.createElement("button");
				overlay.type         = "button";
				overlay.className    = "wc-unmute-overlay";
				overlay.setAttribute("aria-label", "Unmute video");
				overlay.innerHTML = `
					<span class="wc-unmute-icon" aria-hidden="true">
						<svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
							<path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
						</svg>
					</span>
					<span class="wc-unmute-label">Tap to unmute</span>
				`;

				const removeOverlay = () => {
					videoEl.muted  = false;
					videoEl.volume = 1;
					videoEl.removeAttribute("muted");
					overlay.remove();
				};

				overlay.addEventListener("click", (e) => {
					e.preventDefault();
					e.stopPropagation();
					removeOverlay();
				});

				/* Any click elsewhere on the page also restores sound. */
				const globalHandler = () => {
					if (!document.body.contains(overlay)) return;
					removeOverlay();
					document.removeEventListener("pointerdown", globalHandler, true);
				};
				document.addEventListener("pointerdown", globalHandler, true);

				container.appendChild(overlay);
			}).catch(() => { /* muted play also failed — nothing we can do */ });
		});
	}

	function parseScheduleHTML(html) {
		const parser = new DOMParser();
		const doc    = parser.parseFromString(html, "text/html");
		const table  = doc.querySelector(".views-table");
		if (!table) return { sessions: [], table: null };

		const rows     = [...table.querySelectorAll("tr")].filter((r) => r.querySelector("td"));
		const sessions = [];

		rows.forEach((row) => {
			const cols = row.querySelectorAll("td");
			if (cols.length < 2) return;

			const dateTd = cols[0];
			const infoTd = cols[1];

			const timeEl       = dateTd.querySelector("time");
			const datetimeISO  = timeEl?.getAttribute("datetime") || "";
			const dateTextCore = timeEl?.textContent.trim() || "";
			const extraText    = dateTd.textContent.replace(dateTextCore, "").trim();

			let dateDay  = "";
			let timeText = "";

			if (dateTextCore.includes(" - ")) {
				const parts = dateTextCore.split(" - ");
				dateDay  = parts[0].trim();
				timeText = parts[1].trim();
			} else {
				dateDay = dateTextCore.trim();
			}

			const dateDisplay = dateTextCore + (extraText ? " " + extraText : "");

			const firstDiv = infoTd.querySelector("div");
			const strong   = firstDiv?.querySelector("strong");
			const title    = strong?.textContent.trim() || infoTd.textContent.trim();

			let room = "";
			if (firstDiv) {
				const htmlInner  = firstDiv.innerHTML;
				const matchParen = htmlInner.match(/<\/strong>\s*\(([^)]+)\)/);
				if (matchParen) {
					room = matchParen[1].trim();
				} else {
					const badge = firstDiv.querySelector(".room-badge");
					if (badge) room = badge.textContent.trim();
				}
			}

			const langLinks = [...infoTd.querySelectorAll("a.video-link")].map((a) => ({
				label: a.textContent.replace(/\[|\]/g, "").trim(),
				b64:   a.dataset.webUrl    || "",
				type:  a.dataset.videoType || "archive",
			}));

			if (!title) return;

			sessions.push({ title, room, dateDay, timeText, dateDisplay, datetimeISO, langLinks });
		});

		return { sessions, table };
	}

	/* =====================================================================
	   DEEP LINK URL PARAMS
	   Supported:
	     ?wc_play=LABEL         → single mode: auto-play that language
	     ?wc_session=TITLE      → all mode: scroll to that session card
	     ?wc_session=TITLE&wc_play=LABEL → all mode: scroll + auto-play

	   Works even when the block is inside an accordion or modal that's
	   closed on page load — it waits until the block becomes visible.
	   URL syntax when combining with a hash:
	     ?wc_play=English#live    (query BEFORE hash — standard URL format)
	===================================================================== */

	const _urlParams    = new URLSearchParams(window.location.search);
	const _urlWcPlay    = (_urlParams.get("wc_play")    || "").trim();
	const _urlWcSession = (_urlParams.get("wc_session") || "").trim();
	const _urlHash      = (window.location.hash || "").replace(/^#/, "").trim();

	function _normLabel(s) {
		return (s || "").replace(/[\u25BA\u25A0]/g, "").trim().toLowerCase();
	}

	/* Try to open an accordion/modal/tab container that matches #hash.
	   Covers common patterns without needing to know any specific plugin. */
	function _triggerHashOpen() {
		if (!_urlHash) return;
		const target = document.getElementById(_urlHash);
		if (!target) return;

		/* Click a toggle that controls the target, if any. */
		const toggleSelectors = [
			`[href="#${_urlHash}"]`,
			`[data-target="#${_urlHash}"]`,
			`[data-bs-target="#${_urlHash}"]`,
			`[aria-controls="${_urlHash}"]`,
			`[data-toggle-target="#${_urlHash}"]`,
		];
		toggleSelectors.forEach((sel) => {
			document.querySelectorAll(sel).forEach((el) => {
				try { el.click(); } catch {}
			});
		});

		/* Walk up to ancestors that could be collapsible and try opening them. */
		let node = target;
		while (node && node !== document.body) {
			if (node.classList) {
				/* Common accordion classes — open by adding .open / .is-open / .active */
				const openable = node.classList.contains("accordion-content")
					|| node.classList.contains("wp-block-brblocks-accordion")
					|| node.classList.contains("collapse")
					|| node.classList.contains("modal")
					|| node.classList.contains("modal-content")
					|| node.classList.contains("tab-pane");
				if (openable) {
					node.classList.add("open", "is-open", "active", "show", "is-visible");
				}
			}
			node = node.parentElement;
		}
	}

	_triggerHashOpen();

	/* Resolves when the element is actually visible on screen (not display:none,
	   not visibility:hidden, has layout box). Times out after 30s to avoid leaks. */
	function _whenVisible(el) {
		return new Promise((resolve) => {
			function isVisible() {
				if (!el.isConnected) return false;
				const rect = el.getBoundingClientRect();
				if (rect.width === 0 && rect.height === 0) return false;
				const cs = getComputedStyle(el);
				if (cs.display === "none" || cs.visibility === "hidden") return false;
				return true;
			}

			if (isVisible()) { resolve(); return; }

			let done = false;
			const finish = () => {
				if (done) return;
				done = true;
				mo.disconnect();
				clearTimeout(timer);
				resolve();
			};

			const mo = new MutationObserver(() => {
				if (isVisible()) finish();
			});
			mo.observe(document.body, {
				attributes:      true,
				attributeFilter: ["class", "style", "hidden", "aria-hidden", "open"],
				subtree:         true,
				childList:       true,
			});

			const timer = setTimeout(finish, 30000);
		});
	}

	/* =====================================================================
	   PROCESS EACH LIVE BLOCK
	===================================================================== */

	document.querySelectorAll(".webcast-live-block").forEach(async (block) => {
		const baseUrl = block.dataset.webcastUrl;
		if (!baseUrl) return;

		const displayMode     = block.dataset.displayMode     || "all";
		const selectedEpisode = block.dataset.selectedEpisode || "";

		/* =================================================================
		   SINGLE MODE
		================================================================= */

		if (displayMode === "single") {
			const playerArea  = block.querySelector(".wc-single-player-area");
			const placeholder = block.querySelector(".wc-single-placeholder");
			const videoEl     = block.querySelector(".wc-single-video");
			const langsBox    = block.querySelector(".wc-single-langs");
			const episodeEl   = block.querySelector(".wc-single-episode-label");

			if (!playerArea || !videoEl || !langsBox) return;

			let html = "";
			try {
				const res = await fetch(baseUrl, { mode: "cors" });
				html = await res.text();
			} catch {
				langsBox.innerHTML = `<span class="wc-single-error">Unable to load schedule.</span>`;
				return;
			}

			const { sessions } = parseScheduleHTML(html);

			if (!sessions.length) {
				langsBox.innerHTML = `<span class="wc-single-error">No live meeting planned yet. Please come back later.</span>`;
				return;
			}

			/* ----- Always resolve to exactly ONE session ----- */
			let targetSession = null;
			if (selectedEpisode) {
				targetSession = sessions.find((s) => s.title === selectedEpisode);
			}
			if (!targetSession) {
				targetSession = sessions[0];  // fallback to first when no pick or no match
			}

			/* ----- Update or create the episode label with full info ----- */
			const labelText = targetSession.title
				+ (targetSession.room         ? ` (${targetSession.room})` : "")
				+ (targetSession.dateDisplay  ? ` \u2014 ${targetSession.dateDisplay}` : "");

			let labelEl = episodeEl;
			if (!labelEl) {
				labelEl = document.createElement("p");
				labelEl.className = "wc-single-episode-label";
				const body    = block.querySelector(".wc-single-body");
				const titleEl = block.querySelector(".wc-single-title");
				if (body) {
					if (titleEl) titleEl.after(labelEl);
					else         body.prepend(labelEl);
				}
			}
			labelEl.textContent = labelText;

			function playSingleVideo(url, isLive) {
				if (videoEl._hlsInstance) {
					videoEl._hlsInstance.destroy();
					videoEl._hlsInstance = null;
				}

				if (isLive && /\.mp4(\?|$)/i.test(url)) {
					url = url.replace(/\.mp4(\?|$)/i, "/index.m3u8$1");
				}

				const isHLS = /\.m3u8(\?|$)/i.test(url) || isLive === true;

				if (isHLS && Hls.isSupported()) {
					const hls = new Hls({ enableWorker: true, lowLatencyMode: true });
					hls.loadSource(url);
					hls.attachMedia(videoEl);

					hls.on(Hls.Events.MANIFEST_PARSED, () => {
						tryPlayWithAutoplayFallback(videoEl, playerArea);
					});

					hls.on(Hls.Events.ERROR, (_event, data) => {
						if (data.fatal) {
							switch (data.type) {
								case Hls.ErrorTypes.NETWORK_ERROR: hls.startLoad();       break;
								case Hls.ErrorTypes.MEDIA_ERROR:   hls.recoverMediaError(); break;
								default:                           hls.destroy();          break;
							}
						}
					});

					videoEl._hlsInstance = hls;
				} else if (isHLS && videoEl.canPlayType("application/vnd.apple.mpegurl")) {
					videoEl.src = url;
					videoEl.addEventListener("loadedmetadata", function onMeta() {
						tryPlayWithAutoplayFallback(videoEl, playerArea);
						videoEl.removeEventListener("loadedmetadata", onMeta);
					});
				} else {
					videoEl.src = url;
					videoEl.load();
					tryPlayWithAutoplayFallback(videoEl, playerArea);
				}
			}

			langsBox.innerHTML = "";

			function buildLangBtn(info, session) {
				let decodedUrl = "";
				try { decodedUrl = atob(info.b64); } catch {}

				const btn = document.createElement("button");
				btn.type      = "button";
				btn.className = "wc-single-lang-btn";
				btn.innerHTML = `<span class="wc-single-lang-icon">\u25BA</span>${info.label}`;

				btn.addEventListener("click", () => {
					if (btn.classList.contains("active")) {
						videoEl.pause();
						btn.classList.remove("active");
						btn.querySelector(".wc-single-lang-icon").textContent = "\u25BA";
						return;
					}

					if (!decodedUrl) return;

					block.querySelectorAll(".wc-single-lang-btn").forEach((b) => {
						b.classList.remove("active");
						const ic = b.querySelector(".wc-single-lang-icon");
						if (ic) ic.textContent = "\u25BA";
					});

					btn.classList.add("active");
					btn.querySelector(".wc-single-lang-icon").textContent = "\u25A0";

					if (placeholder) placeholder.classList.add("wc-hidden");
					videoEl.classList.remove("wc-hidden");

					notifyPlaybackStart(block);
					playSingleVideo(decodedUrl, info.type === "live");
				});

				return btn;
			}

			/* ----- Render language buttons for the ONE target session ----- */
			targetSession.langLinks.forEach((info) => {
				langsBox.appendChild(buildLangBtn(info, targetSession));
			});

			/* ----- Deep link auto-play: ?wc_play=LABEL
			   Waits for the block to become visible (accordion open, modal shown, etc.). */
			if (_urlWcPlay) {
				_whenVisible(block).then(() => {
					const target = [...langsBox.querySelectorAll(".wc-single-lang-btn")]
						.find((b) => _normLabel(b.textContent) === _normLabel(_urlWcPlay));
					if (target) {
						setTimeout(() => {
							block.scrollIntoView({ behavior: "smooth", block: "center" });
							target.click();
						}, 250);
					}
				});
			}

			const playBtn = block.querySelector(".wc-single-play-btn");
			if (playBtn) {
				playBtn.style.cursor = "pointer";
				playBtn.addEventListener("click", () => {
					const firstBtn = langsBox.querySelector(".wc-single-lang-btn");
					if (firstBtn) firstBtn.click();
				});
			}

			document.addEventListener("wc-playback-start", (e) => {
				if (e.detail && e.detail.source !== block) {
					videoEl.pause();
					block.querySelectorAll(".wc-single-lang-btn").forEach((b) => {
						b.classList.remove("active");
						const ic = b.querySelector(".wc-single-lang-icon");
						if (ic) ic.textContent = "\u25BA";
					});
				}
			});

			const parentAccordionContentSingle = block.closest(".accordion-content");
			if (parentAccordionContentSingle) {
				function refreshAccordionHeightSingle() {
					const parentAccordion = parentAccordionContentSingle.closest(".wp-block-brblocks-accordion");
					if (parentAccordion?.classList.contains("open")) {
						requestAnimationFrame(() => {
							requestAnimationFrame(() => {
								parentAccordionContentSingle.style.maxHeight = parentAccordionContentSingle.scrollHeight + "px";
							});
						});
					}
				}
				const roSingle = new ResizeObserver(refreshAccordionHeightSingle);
				roSingle.observe(block);
			}

			return;
		}

		/* =================================================================
		   ALL MODE — original behaviour
		================================================================= */

		const showSearch = block.dataset.showSearch === "true";
		const showTabs   = block.dataset.showTabs   === "true";
		const showMenus  = block.dataset.showMenus  === "true";

		const scheduleBox = block.querySelector(".wc-schedule");
		const inlineBox   = block.querySelector(".wc-inline-player");
		const video       = block.querySelector(".wc-inline-video");
		const placeholder = block.querySelector(".wc-placeholder");
		const storageKey  = `wc-playback-${baseUrl}`;

		const savedTime = sessionStorage.getItem(storageKey);
		let lastPlaybackTime = savedTime ? parseInt(savedTime, 10) : 0;

		if (!scheduleBox || !inlineBox || !video) return;

		document.addEventListener("wc-playback-start", (e) => {
			if (e.detail.source !== block) closeVideo();
		});

		function closeVideo() {
			block.querySelectorAll(".lang-tag").forEach((t) => {
				t.classList.remove("active");
				const icon = t.querySelector(".lang-play");
				if (icon) icon.textContent = "\u25BA";
			});
			stopPlayer(inlineBox, video, scheduleBox, placeholder);
			lastPlaybackTime = 0;
			sessionStorage.removeItem(storageKey);
			history.replaceState(null, "", window.location.pathname);
		}

		function playUrl(url, fromTime, isLive) {
			notifyPlaybackStart(block);

			const startTime = (fromTime !== undefined && fromTime !== null) ? fromTime : lastPlaybackTime;

			if (isLive && /\.mp4(\?|$)/i.test(url)) {
				url = url.replace(/\.mp4(\?|$)/i, "/index.m3u8$1");
			}

			const isHLS = /\.m3u8(\?|$)/i.test(url) || isLive === true;

			inlineBox.classList.remove("wc-inline-hidden");
			inlineBox.classList.add("wc-inline-visible");
			if (placeholder) placeholder.classList.add("wc-placeholder-hidden");

			const cleanNew     = url.split("?")[0].split("#")[0];
			const cleanCurrent = (video.src || "").split("?")[0].split("#")[0];

			if (cleanNew === cleanCurrent) {
				video.currentTime = startTime;
				tryPlayWithAutoplayFallback(video, inlineBox);
			} else {
				if (video._hlsInstance) {
					video._hlsInstance.destroy();
					video._hlsInstance = null;
				}

				if (isHLS && Hls.isSupported()) {
					const hls = new Hls({ enableWorker: true, lowLatencyMode: true });
					hls.loadSource(url);
					hls.attachMedia(video);

					hls.on(Hls.Events.MANIFEST_PARSED, () => {
						if (startTime > 0 && !isLive) video.currentTime = startTime;
						tryPlayWithAutoplayFallback(video, inlineBox);
					});

					hls.on(Hls.Events.ERROR, (_event, data) => {
						if (data.fatal) {
							switch (data.type) {
								case Hls.ErrorTypes.NETWORK_ERROR:
									console.warn("[WC] HLS network error – retrying…");
									hls.startLoad();
									break;
								case Hls.ErrorTypes.MEDIA_ERROR:
									console.warn("[WC] HLS media error – recovering…");
									hls.recoverMediaError();
									break;
								default:
									console.error("[WC] Fatal HLS error", data);
									hls.destroy();
									break;
							}
						}
					});

					video._hlsInstance = hls;

				} else if (isHLS && video.canPlayType("application/vnd.apple.mpegurl")) {
					video.src = url;
					video.addEventListener("loadedmetadata", function onMeta() {
						if (startTime > 0 && !isLive) video.currentTime = startTime;
						tryPlayWithAutoplayFallback(video, inlineBox);
						video.removeEventListener("loadedmetadata", onMeta);
					});

				} else {
					video.src = url;
					video.load();
					if (startTime > 0) {
						video.addEventListener("loadedmetadata", function onMeta() {
							video.currentTime = startTime;
							tryPlayWithAutoplayFallback(video, inlineBox);
							video.removeEventListener("loadedmetadata", onMeta);
						});
					} else {
						tryPlayWithAutoplayFallback(video, inlineBox);
					}
				}
			}

			inlineBox.scrollIntoView({ behavior: "smooth", block: "center" });
		}

		window.addEventListener("popstate", (e) => {
			if (window.location.hash) return;
			if (e.state?.video) {
				playUrl(e.state.video, e.state.time || 0, e.state.live || false);
			} else {
				closeVideo();
			}
		});

		document.addEventListener("keydown", (e) => {
			if (
				e.key === "Backspace" &&
				!["INPUT", "TEXTAREA"].includes(document.activeElement.tagName)
			) {
				e.preventDefault();
				closeVideo();
			}
		});

		setInterval(() => {
			if (!video.paused && video.currentTime > 0) {
				lastPlaybackTime = Math.floor(video.currentTime);
				sessionStorage.setItem(storageKey, lastPlaybackTime);
			}
		}, 5000);

		scheduleBox.innerHTML = `<div class="wc-loading">Loading schedule\u2026</div>`;

		let html = "";
		try {
			const res = await fetch(baseUrl, { mode: "cors" });
			html = await res.text();
		} catch {
			scheduleBox.innerHTML = `<div class="wc-error">Unable to load schedule.</div>`;
			return;
		}

		const { sessions, table } = parseScheduleHTML(html);

		if (!table) {
			scheduleBox.innerHTML = `<div class="wc-error">No live meeting planned yet. Please come back later.</div>`;
			return;
		}

		function setupTableLinks(tableEl) {
			tableEl.querySelectorAll("a.video-link").forEach((a) => {
				const b64 = a.dataset.webUrl;
				if (!b64) return;
				let url = "";
				try { url = atob(b64); } catch { return; }
				a.removeAttribute("href");
				a.dataset.streamUrl = url;
				a.classList.add("wc-lang-choice");
				a.addEventListener("click", (e) => {
					e.preventDefault();
					if (!url) return;
					playUrl(url, 0);
				});
			});
		}

		if (!sessions.length) {
			scheduleBox.innerHTML = "";
			const cloned = table.cloneNode(true);
			setupTableLinks(cloned);
			scheduleBox.appendChild(cloned);
			return;
		}

		const uniqueDates    = [...new Set(sessions.map((s) => s.dateDay))];
		const uniqueRooms    = [...new Set(sessions.map((s) => s.room).filter(Boolean))];
		const uniqueSessions = [...new Set(sessions.flatMap((s) => extractAllSessions(s.title)))];

		const filterState = { search: "", date: "", room: "", session: "" };

		const filtersWrapper = document.createElement("div");
		filtersWrapper.className = "wc-filters";

		const grid = document.createElement("div");
		grid.className = "wc-grid";

		const searchWrap = document.createElement("div");
		searchWrap.className = "wc-search-wrapper";

		const searchIcon = document.createElement("span");
		searchIcon.className = "wc-search-icon";
		searchIcon.innerHTML = `<svg width="18" height="18" viewBox="0 0 24 24">
			<circle cx="11" cy="11" r="8" stroke="#999" stroke-width="2" fill="none"/>
			<line x1="16" y1="16" x2="22" y2="22" stroke="#999" stroke-width="2"/>
		</svg>`;

		const searchInput = document.createElement("input");
		searchInput.type        = "search";
		searchInput.placeholder = "Search sessions\u2026";
		searchInput.className   = "wc-search-input";

		searchInput.addEventListener("input", () => {
			filterState.search = searchInput.value.toLowerCase();
			renderCards();
		});

		searchWrap.appendChild(searchIcon);
		searchWrap.appendChild(searchInput);

		const daysWrap = document.createElement("div");
		daysWrap.className = "wc-days-tabs";

		const allBtn = document.createElement("button");
		allBtn.type        = "button";
		allBtn.className   = "wc-day-tab active";
		allBtn.textContent = "All days";

		allBtn.addEventListener("click", () => {
			filterState.date = "";
			daysWrap.querySelectorAll(".wc-day-tab").forEach((b) => b.classList.remove("active"));
			allBtn.classList.add("active");
			renderCards();
		});

		daysWrap.appendChild(allBtn);

		uniqueDates.forEach((d, i) => {
			const btn = document.createElement("button");
			btn.type         = "button";
			btn.className    = "wc-day-tab";
			btn.textContent  = `${formatDateLabel(d)} (Day ${i + 1})`;
			btn.dataset.date = d;

			btn.addEventListener("click", () => {
				filterState.date = d;
				daysWrap.querySelectorAll(".wc-day-tab").forEach((b) => b.classList.remove("active"));
				btn.classList.add("active");
				renderCards();
			});

			daysWrap.appendChild(btn);
		});

		const daysSelectWrap = document.createElement("div");
		daysSelectWrap.className = "wc-days-select-wrap";

		const daysSelect = document.createElement("select");
		daysSelect.className = "wc-days-select";

		const optAllDays       = document.createElement("option");
		optAllDays.value       = "";
		optAllDays.textContent = "All days";
		daysSelect.appendChild(optAllDays);

		uniqueDates.forEach((d, index) => {
			const opt       = document.createElement("option");
			opt.value       = d;
			opt.textContent = `${formatDateLabel(d)} (Day ${index + 1})`;
			daysSelect.appendChild(opt);
		});

		daysSelect.addEventListener("change", () => {
			filterState.date = daysSelect.value;
			daysWrap.querySelectorAll(".wc-day-tab").forEach((t) => t.classList.remove("active"));
			if (daysSelect.value === "") {
				allBtn.classList.add("active");
			} else {
				const tab = [...daysWrap.querySelectorAll(".wc-day-tab")].find(
					(t) => t.dataset.date === daysSelect.value
				);
				if (tab) tab.classList.add("active");
			}
			renderCards();
		});

		daysSelectWrap.appendChild(daysSelect);

		let roomWrap = null;
		if (uniqueRooms.length > 0) {
			roomWrap = document.createElement("div");
			roomWrap.className = "wc-filter-room-inline";

			const selectRoom   = document.createElement("select");
			const optAllRoom   = document.createElement("option");
			optAllRoom.value       = "";
			optAllRoom.textContent = "All rooms";
			selectRoom.appendChild(optAllRoom);

			uniqueRooms.forEach((r) => {
				const o = document.createElement("option");
				o.value = r; o.textContent = r;
				selectRoom.appendChild(o);
			});

			selectRoom.addEventListener("change", () => {
				filterState.room = selectRoom.value;
				renderCards();
			});

			roomWrap.appendChild(selectRoom);
		}

		let sessionWrap = null;
		if (uniqueSessions.length > 0) {
			sessionWrap = document.createElement("div");
			sessionWrap.className = "wc-filter-session-inline";

			const selectSess = document.createElement("select");
			const optAllSess = document.createElement("option");
			optAllSess.value       = "";
			optAllSess.textContent = "All sessions";
			selectSess.appendChild(optAllSess);

			uniqueSessions.forEach((label) => {
				const o = document.createElement("option");
				o.value = label; o.textContent = label;
				selectSess.appendChild(o);
			});

			selectSess.addEventListener("change", () => {
				filterState.session = selectSess.value;
				renderCards();
			});

			sessionWrap.appendChild(selectSess);
		}

		const isMobile = window.matchMedia("(max-width: 768px)").matches;

		if (showSearch) filtersWrapper.appendChild(searchWrap);

		if (!isMobile) {
			if (showTabs) filtersWrapper.appendChild(daysWrap);
			if (showMenus) {
				if (showTabs) {
					if (roomWrap)    daysWrap.appendChild(roomWrap);
					if (sessionWrap) daysWrap.appendChild(sessionWrap);
				} else {
					if (roomWrap)    filtersWrapper.appendChild(roomWrap);
					if (sessionWrap) filtersWrapper.appendChild(sessionWrap);
				}
			}
		} else {
			daysWrap.style.display = "none";
			const mobileRow = document.createElement("div");
			mobileRow.className = "wc-mobile-filters";
			if (showTabs) mobileRow.appendChild(daysSelectWrap);
			if (showMenus) {
				if (roomWrap)    mobileRow.appendChild(roomWrap);
				if (sessionWrap) mobileRow.appendChild(sessionWrap);
			}
			filtersWrapper.appendChild(mobileRow);
		}

		function renderCards() {
			grid.innerHTML = "";

			const filtered = sessions.filter((s) => {
				if (filterState.search && !filterState.session) {
					if (!s.title.toLowerCase().includes(filterState.search)) return false;
				}
				if (filterState.date    && s.dateDay !== filterState.date)  return false;
				if (filterState.room    && s.room    !== filterState.room)   return false;
				if (filterState.session) {
					const found = extractAllSessions(s.title);
					if (!found.includes(filterState.session)) return false;
				}
				return true;
			});

			if (!filtered.length) {
				const empty       = document.createElement("div");
				empty.className   = "wc-empty";
				empty.textContent = "No sessions match your filters.";
				grid.appendChild(empty);
				return;
			}

			filtered.forEach((session) => {
				const card = document.createElement("div");
				card.className = "wc-card";

				card.innerHTML = `
					<div class="wc-card-top">
						<h4 class="wc-card-title">${session.title}</h4>
						<div class="wc-room">${session.room || ""}</div>
					</div>
					<div class="wc-card-bottom">
						<div class="wc-languages"></div>
						<div class="wc-date">${session.dateDisplay}</div>
					</div>
				`;

				const langBox = card.querySelector(".wc-languages");

				session.langLinks.forEach((info) => {
					let decodedUrl = "";
					try { decodedUrl = atob(info.b64); } catch {}

					const tag     = document.createElement("span");
					tag.className = "lang-tag";
					tag.innerHTML = `<span class="lang-play">\u25BA</span>${info.label}`;

					const icon = tag.querySelector(".lang-play");

					tag.addEventListener("click", () => {
						const alreadyActive = tag.classList.contains("active");

						if (alreadyActive) {
							tag.classList.remove("active");
							if (icon) icon.textContent = "\u25BA";
							stopPlayer(inlineBox, video, scheduleBox, placeholder);
							return;
						}

						const currentTime = (video.src && !video.paused)
							? Math.floor(video.currentTime)
							: lastPlaybackTime;

						block.querySelectorAll(".lang-tag").forEach((t) => {
							t.classList.remove("active");
							const ic = t.querySelector(".lang-play");
							if (ic) ic.textContent = "\u25BA";
						});

						tag.classList.add("active");
						if (icon) icon.textContent = "\u25A0";

						if (!decodedUrl) return;

						lastPlaybackTime = currentTime;
						sessionStorage.setItem(storageKey, lastPlaybackTime);

						const cleanNew     = decodedUrl.split("?")[0].split("#")[0];
						const cleanCurrent = (video.src || "").split("?")[0].split("#")[0];
						if (cleanNew !== cleanCurrent) {
							history.pushState(
								{ video: decodedUrl, time: lastPlaybackTime, live: info.type === "live" },
								"",
								window.location.pathname
							);
						}

						playUrl(decodedUrl, lastPlaybackTime, info.type === "live");
					});

					langBox.appendChild(tag);
				});

				grid.appendChild(card);
			});
		}

		scheduleBox.innerHTML = "";
		scheduleBox.appendChild(filtersWrapper);
		scheduleBox.appendChild(grid);
		renderCards();

		/* ----- Deep link in all mode: ?wc_session=TITLE[&wc_play=LABEL]
		   Waits for the block to become visible (accordion, modal, etc.). */
		if (_urlWcSession) {
			_whenVisible(block).then(() => {
				const targetTitle = _urlWcSession.toLowerCase();
				const cards = grid.querySelectorAll(".wc-card");
				for (const card of cards) {
					const titleEl = card.querySelector(".wc-card-title");
					if (titleEl && titleEl.textContent.trim().toLowerCase() === targetTitle) {
						setTimeout(() => {
							card.scrollIntoView({ behavior: "smooth", block: "center" });
							card.style.outline = "2px solid #009cd6";
							card.style.outlineOffset = "4px";
							setTimeout(() => {
								card.style.outline = "";
								card.style.outlineOffset = "";
							}, 2500);

							if (_urlWcPlay) {
								const tag = [...card.querySelectorAll(".lang-tag")]
									.find((t) => _normLabel(t.textContent) === _normLabel(_urlWcPlay));
								if (tag) setTimeout(() => tag.click(), 400);
							}
						}, 250);
						break;
					}
				}
			});
		}

		const parentAccordionContent = block.closest(".accordion-content");
		if (parentAccordionContent) {
			function refreshAccordionHeight() {
				const parentAccordion = parentAccordionContent.closest(".wp-block-brblocks-accordion");
				if (parentAccordion?.classList.contains("open")) {
					requestAnimationFrame(() => {
						requestAnimationFrame(() => {
							parentAccordionContent.style.maxHeight = parentAccordionContent.scrollHeight + "px";
						});
					});
				}
			}
			const ro = new ResizeObserver(refreshAccordionHeight);
			ro.observe(block);
		}
	});
});